import { useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { RefreshCw, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@reflex/ui/components/ui/button';
import { EmptyState } from '@reflex/ui/components/shared/EmptyState';
import { formatRelativeTime } from '@reflex/ui/lib/format';
import { AsyncContent } from '@/components/shared/AsyncContent';
import { api } from '@/lib/api';
import { mutateWithToast } from '@/lib/mutation-helpers';
import { queryKeys } from '@/lib/query-keys';
import type { InviteShare } from './inviteShare';

interface OrganizationInviteListProps {
  organizationId: string;
  /**
   * When false the rows render without a revoke button. Required rather than
   * defaulted: the callers resolve it from different authority models (the
   * org page from `members:manage` with the platform-admin bypass
   * `usePermission` applies, the admin panel from the platform-admin gate on
   * the route itself), and a default would silently pick one of them for
   * whoever mounts this next.
   */
  canRevoke: boolean;
  /** Called with the rotated link a resend mints, for the caller to surface. */
  onShare: (share: InviteShare) => void;
}

/** Invites that have been sent but not yet redeemed, with resend and revoke. */
export function OrganizationInviteList({
  organizationId,
  canRevoke,
  onShare,
}: OrganizationInviteListProps) {
  const queryClient = useQueryClient();
  const queryKey = queryKeys.organizationInvites(organizationId);

  const invitesQuery = useQuery({
    queryKey,
    // `active`, not the default `pending`: an invite whose link lapsed is
    // the one most worth resending, and filtering it out would leave the
    // address looking uninvited with nothing to act on.
    queryFn: () => api.listOrganizationInvites(organizationId, { status: 'active' }),
    enabled: organizationId.length > 0,
  });

  const resendInviteMutation = useMutation({
    mutationFn: (inviteId: string) => api.resendOrganizationInvite(organizationId, inviteId),
    onSuccess: (result) => {
      void queryClient.invalidateQueries({ queryKey });
      onShare(result);
    },
    onError: (error: Error) => {
      // A resend usually fails because the invite stopped being pending;
      // refetch rather than leave a row that can only fail again.
      void queryClient.invalidateQueries({ queryKey });
      toast.error('Failed to resend invite', { description: error.message });
    },
  });

  // Only to name the roles the rows already reference. Shares the members
  // page's query key, so the two surfaces resolve to one fetch.
  const rolesQuery = useQuery({
    queryKey: ['organizations', organizationId, 'roles'],
    queryFn: () => api.listOrganizationRoles(organizationId),
    enabled: organizationId.length > 0,
  });
  const roleNameById = useMemo(
    () => new Map((rolesQuery.data ?? []).map((role) => [role.id, role.name])),
    [rolesQuery.data],
  );

  const revokeInviteMutation = useMutation({
    mutationFn: (inviteId: string) => api.revokeOrganizationInvite(organizationId, inviteId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey });
    },
  });

  const invites = invitesQuery.data ?? [];

  return (
    <div data-testid="pending-invites-section">
      <AsyncContent
        isLoading={invitesQuery.isLoading}
        isError={invitesQuery.isError}
        loadingMessage="Loading invites…"
        errorMessage="Failed to load invites."
      >
        {invites.length === 0 ? (
          <EmptyState
            variant="bordered"
            title="No open invites"
            description="Invites you send appear here until they are redeemed or expire."
          />
        ) : (
          <ul className="divide-y divide-border rounded-md border">
            {invites.map((invite) => (
              <li
                key={invite.id}
                className="flex items-center justify-between gap-3 px-3 py-2"
                data-testid={`pending-invite-row-${invite.id}`}
              >
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium">{invite.email}</p>
                  <p className="truncate text-xs text-muted-foreground">
                    Invited {formatRelativeTime(invite.createdAt)} ·{' '}
                    {invite.status === 'expired'
                      ? 'expired'
                      : `expires ${formatRelativeTime(invite.expiresAt)}`}{' '}
                    · token ending …{invite.tokenLast4}
                    {/*
                     * Named from the org's own role list, not the id: the id
                     * is meaningless to an admin. A role deleted since the
                     * invite was sent has no name to show, and none is shown —
                     * matching redemption, which drops it and joins the
                     * invitee as a plain member.
                     */}
                    {invite.roleId && roleNameById.get(invite.roleId)
                      ? ` · joins as ${roleNameById.get(invite.roleId)}`
                      : ''}
                  </p>
                </div>
                <div className="flex shrink-0 items-center gap-1">
                  {/*
                   * Ungated, unlike revoke: resending needs exactly the
                   * `members:invite` the list read already required, and
                   * anyone seeing this panel can retype the address into
                   * the send form for the same effect.
                   */}
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => resendInviteMutation.mutate(invite.id)}
                    // Per-row busy state, matching revoke below.
                    disabled={
                      resendInviteMutation.isPending && resendInviteMutation.variables === invite.id
                    }
                    aria-label={`Resend invite for ${invite.email}`}
                  >
                    <RefreshCw className="icon-sm" />
                  </Button>
                  {canRevoke && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() =>
                        mutateWithToast(revokeInviteMutation, invite.id, {
                          success: 'Invite revoked',
                          error: 'Failed to revoke invite',
                        })
                      }
                      // Only the row being revoked goes busy; one in-flight
                      // revoke used to disable every other row's button.
                      disabled={
                        revokeInviteMutation.isPending &&
                        revokeInviteMutation.variables === invite.id
                      }
                      aria-label={`Revoke invite for ${invite.email}`}
                    >
                      <Trash2 className="icon-sm" />
                    </Button>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </AsyncContent>
    </div>
  );
}
